import type {
  GuidedAgeUnit,
  GuidedAssessmentPath,
  GuidedMaternalStatus,
  GuidedPatientSex,
} from '../types/guided-mode.types'

// Match the PWA age conversion and the clinical growth tables.
const { DAYS_PER_MONTH, DAYS_PER_YEAR } = require('@shared/clinical-logic/convert/utilities/convertConstants')
const DAYS_PER_UNIT: Record<GuidedAgeUnit, number> = {
  days: 1,
  months: DAYS_PER_MONTH,
  years: DAYS_PER_YEAR,
}
export const MAX_AGE_BY_UNIT: Readonly<Record<GuidedAgeUnit, number>> = Object.freeze({
  days: 95 * DAYS_PER_YEAR,
  months: 95 * 12,
  years: 95,
})
export const NEWBORN_MAX_DAYS = 60
const CHILD_MAX_DAYS_EXCLUSIVE = 5 * DAYS_PER_YEAR
const YOUTH_MAX_DAYS_EXCLUSIVE = 15 * DAYS_PER_YEAR
const NEWBORN_MAX_MONTHS_EXCLUSIVE = 2
const CHILD_MAX_MONTHS_EXCLUSIVE = 60
const YOUTH_MAX_MONTHS_EXCLUSIVE = 180
const NEWBORN_MAX_YEARS_EXCLUSIVE = 0.2
const CHILD_MAX_YEARS_EXCLUSIVE = 5
const YOUTH_MAX_YEARS_EXCLUSIVE = 15
const MATERNAL_STATUS_MIN_AGE: Record<GuidedAgeUnit, number> = {
  days: 15 * DAYS_PER_YEAR,
  months: 180,
  years: 15,
}
const MATERNAL_STATUS_MAX_AGE: Record<GuidedAgeUnit, number> = {
  days: 50 * DAYS_PER_YEAR,
  months: 50 * 12,
  years: 50,
}

export interface GuidedPatientDemographics {
  age: number | undefined
  ageUnit: GuidedAgeUnit
  sex: GuidedPatientSex
  maternalStatus: GuidedMaternalStatus
}

export function isValidAge(age: number | undefined, unit: GuidedAgeUnit): boolean {
  return age !== undefined
    && Number.isFinite(age)
    && age > 0
    && age <= MAX_AGE_BY_UNIT[unit]
}

export function convertAgeToDays(age: number | undefined, unit: GuidedAgeUnit): number | null {
  if (!isValidAge(age, unit)) return null

  return Math.round(age! * DAYS_PER_UNIT[unit])
}

export function shouldCollectMaternalStatus(
  age: number | undefined,
  unit: GuidedAgeUnit,
  sex: GuidedPatientSex,
): boolean {
  return sex === 'female'
    && isValidAge(age, unit)
    && age !== undefined
    && age >= MATERNAL_STATUS_MIN_AGE[unit]
    && age <= MATERNAL_STATUS_MAX_AGE[unit]
}

function selectPathByAge(age: number, unit: GuidedAgeUnit): GuidedAssessmentPath {
  if (unit === 'days') {
    if (age <= NEWBORN_MAX_DAYS) return 'newborn'
    if (age < CHILD_MAX_DAYS_EXCLUSIVE) return 'child'
    if (age < YOUTH_MAX_DAYS_EXCLUSIVE) return 'youth'
    return 'adult'
  }

  if (unit === 'months') {
    if (age < NEWBORN_MAX_MONTHS_EXCLUSIVE) return 'newborn'
    if (age < CHILD_MAX_MONTHS_EXCLUSIVE) return 'child'
    if (age < YOUTH_MAX_MONTHS_EXCLUSIVE) return 'youth'
    return 'adult'
  }

  if (age < NEWBORN_MAX_YEARS_EXCLUSIVE) return 'newborn'
  if (age < CHILD_MAX_YEARS_EXCLUSIVE) return 'child'
  if (age < YOUTH_MAX_YEARS_EXCLUSIVE) return 'youth'
  return 'adult'
}

export function selectAssessmentPath(demographics: GuidedPatientDemographics): GuidedAssessmentPath | null {
  const ageInDays = convertAgeToDays(demographics.age, demographics.ageUnit)
  if (ageInDays === null || !demographics.sex) return null

  if (shouldCollectMaternalStatus(demographics.age, demographics.ageUnit, demographics.sex)) {
    if (demographics.maternalStatus === 'antenatal') return 'antenatal'
    if (demographics.maternalStatus === 'postnatal') return 'postnatal'
    if (!demographics.maternalStatus) return null
  }
  return selectPathByAge(demographics.age!, demographics.ageUnit)
}

export function isAgeWithinAssessmentPath(
  age: number | undefined,
  unit: GuidedAgeUnit,
  path: GuidedAssessmentPath,
): boolean {
  if (!isValidAge(age, unit) || age === undefined) return false
  if (path === 'antenatal' || path === 'postnatal') {
    return shouldCollectMaternalStatus(age, unit, 'female')
  }
  return selectPathByAge(age, unit) === path
}
